/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Nikolay Sviridov
 * @version 1.33.0
 * @since 1.33.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';

import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import { ModalMultipleOrders } from '@/main/modules/modal/components/ModalMultipleOrders';
import { modalOpen } from '@/main/modules/modal/modalActions';

import {
    loadTableFieldsSuccessful,
    loadTableListAction,
    loadTableListError,
    loadTableListSuccessful,
    setTableCurrentName,
    tableResetAction,
} from '../../components/Table/actions/tableActions';
import { TABLE_DENSITY_LOAD_SIZE, TABLE_LOAD_SIZE } from '../../components/Table/constants';
import { TABLE_ROW_DELETE_ACTION, TABLE_ROW_RESTORE_ACTION } from '../../components/Table/constants/actionConstants';
import {
    makeSelectorTableCurrentName,
    makeSelectorTableData,
    makeSelectorTableRemovedRowIndexes,
} from '../../components/Table/selectors/tableSelectors';
import {
    TableNameType,
    TLoadTableListAction,
    TTableRowDeleteAction,
    TTableRowRestoreAction,
} from '../../components/Table/types/tableTypes';
import {
    API_HOSTNAME,
    IMPORT_SUBSCRIBERS_LIMITS_LIST,
    IMPORT_SUBSCRIBERS_LIMITS_UPLOAD_DOCUMENT,
    WIZARD,
    WIZARD_ACTION,
} from '../../modules/app/Api';
import { EHttpMethod, HttpStatus } from '../../modules/app/appConstants';
import { LOAD_INTERNAL_TRANSLATIONS_SUCCESS } from '../../modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardImportSubscribersLimits,
} from '../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../modules/internationalization/types';
import { IWizardImportSubscribersLimitsTranslation } from '../../modules/internationalization/types/wizards/importSubscribersLimits';
import { logBusinessEvent } from '../../modules/logging/loggingActions';
import { ELogBusinessEventName, ELogBusinessEventType } from '../../modules/logging/loggingConstants';
import { makeSelectIsDensity } from '../../modules/storage/storageSelectors';
import { wizardReportStorageRestoreSaga } from '../../modules/wizard/components/WizardReport/wizardReportSagas';
import { makeSelectWizardReportOrderParams } from '../../modules/wizard/components/WizardReport/wizardReportSelectors';
import { IWizardReportOrderParams } from '../../modules/wizard/components/WizardReport/wizardReportTypes';
import {
    wizardNextAction,
    wizardStorageSaveStepAction,
    wizardStorageUpdateStepAction,
} from '../../modules/wizard/wizardActions';
import {
    EWizardName,
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '../../modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '../../modules/wizard/wizardSagas';
import {
    makeSelectWizardBasketSnapshotHeader,
    makeSelectWizardBasketSnapshotId,
    makeSelectWizardStepName,
} from '../../modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction } from '../../modules/wizard/wizardTypes';
import { requestSaga } from '../../sagas/requestSaga';
import { formQueryParamsString } from '../../sagas/utils/formQueryParamsString';
import { stringFormat } from '../../utils/stringFormat';
import {
    makeSelectAuthoritiesIsServiceUser,
} from '../PageAuth/selectors/authSelectors';
import {
    formImportSubscribersLimitsTableFields,
    formImportSubscribersLimitsTableList,
} from './components/WizardImportSubscribersLimitsTable/WizardImportSubscribersLimitsTableCells';
import { TWizardImportSubscribersLimitsAttachFile } from './types/wizardImportSubscribersLimitsActionTypes';
import { IWizardImportSubscribersLimitsValidatedSubscriberResponse } from './types/wizardImportSubscribersLimitsTypes';
import {
    wizardImportSubscribersLimitsAttachFileFailed,
    wizardImportSubscribersLimitsAttachFileSuccessful,
    wizardImportSubscribersLimitsInitValidatedListFailed,
    wizardImportSubscribersLimitsSetParamsAction,
    wizardImportSubscribersLimitsSetStepValidAction,
    wizardImportSubscribersLimitsStartValidateAction,
} from './wizardImportSubscribersLimitsActions';
import {
    EWizardImportSubscribersLimitsSteps,
    WIZARD_IMPORT_SUBSCRIBERS_LIMITS_ATTACH_FILE,
    WIZARD_IMPORT_SUBSCRIBERS_LIMITS_ATTACH_FILE_FAILED,
    WIZARD_IMPORT_SUBSCRIBERS_LIMITS_HANDLE_NEXT,
    WIZARD_IMPORT_SUBSCRIBERS_LIMITS_INIT_VALIDATED_LIST,
    WIZARD_IMPORT_SUBSCRIBERS_LIMITS_INIT_VALIDATED_LIST_FAILED,
    WIZARD_IMPORT_SUBSCRIBERS_LIMITS_SET_PARAMS,
    WIZARD_IMPORT_SUBSCRIBERS_LIMITS_START_VALIDATE,
} from './wizardImportSubscribersLimitsConstant';
import {
    makeSelectWizardImportSubscribersLimitsErrorRowsAmount,
    makeSelectWizardImportSubscribersLimitsFileName,
    makeSelectWizardImportSubscribersLimitsFileRowsAmount,
    makeSelectWizardImportSubscribersLimitsMaxImportSubscriberAmount,
    makeSelectWizardImportSubscribersLimitsRemovedRowsAmount,
} from './wizardImportSubscribersLimitsSelectors';


/*
 * -------------------------------------------
 *           INITIALIZING
 * -------------------------------------------
 */
export function* wizardImportSubscribersLimitsMetaSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    yield call(wizardImportSubscribersLimitsLoadDataFromStorageSaga);

    switch (stepName) {
        case EWizardImportSubscribersLimitsSteps.SelectionStep:
            const fileTemplateUrl = action.payload.step.fileTemplateUrl;
            const maxImportSubscriberAmount: number = action.payload.step.maxImportSubscriberAmount;
            const importFileFormat = action.payload.step.importFileFormat;
            const maxFileName = action.payload.step.maxFileName;
            const maxFileSize = action.payload.step.maxFileSize;
            const warningSettings = action.payload.step.warningSettings;
            yield put(
                wizardImportSubscribersLimitsSetParamsAction({
                    fileTemplateUrl,
                    maxImportSubscriberAmount,
                    importFileFormat,
                    maxFileName,
                    maxFileSize,
                    warningSettings,
                }),
            );
            break;
        case EWizardImportSubscribersLimitsSteps.ConfirmStep:
            const errorRowsAmount = action.payload.step.errorRowsAmount;
            yield put(
                wizardImportSubscribersLimitsSetParamsAction({
                    errorRowsAmount,
                }),
            );
            yield call(wizardReportStorageRestoreSaga, action);
            break;
    }
}

/*
 * -------------------------------------------
 *       SEND DATA ON NEXT STEP
 * -------------------------------------------
 */
export function* wizardImportSubscribersLimitsHandleNextSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardImportSubscribersLimitsSteps.SelectionStep:
            yield put(
                logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_IMPORT_LIMIT_MASTER_STEP_2,
                    type: ELogBusinessEventType.common,
                }),
            );
            yield put(wizardNextAction({}));
            break;
        case EWizardImportSubscribersLimitsSteps.ConfirmStep:
            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());
            const list = yield select(makeSelectorTableData());
            const removedRowIndexes: number[] = yield select(makeSelectorTableRemovedRowIndexes());

            const params: { [key: string]: number[] } = {
                excludedSubsIds: removedRowIndexes?.map((index) => list[index]?.subsId).filter((id) => id),
            };

            yield put(
                logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_IMPORT_LIMIT_MASTER_CREATE_ORDER,
                    type: ELogBusinessEventType.common,
                }),
            );

            yield put(
                wizardNextAction({
                    data: params,
                    ...reportParams,
                }),
            );
            break;
    }

    yield call(wizardImportSubscribersLimitsUpdateStorageSaga);
}

export function* wizardImportSubscribersLimitsHandleNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardImportSubscribersLimitsSteps.SelectionStep:
            break;
        case EWizardImportSubscribersLimitsSteps.ConfirmStep:
            const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

            const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';
            const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
            const goBack = () => breadCrumbGoBack(breadCrumbsData);

            yield put(
                        modalOpen({
                            render: () =>
                                createElement(ModalMultipleOrders, {
                                    isOpen: true,
                                    orderOrOrders: orderId,
                                    onClose: goBack,
                                    onSuccessButtonClick: breadCrumbGoBack,
                                    orderTypeTranlation: orderCreationModal.success.orderType.IMPORT_SUBSCRIBERS_LIMITS,
                                }),
                        }),
                    );
            break;
    }
}

/*
 * -------------------------------------------
 *       SAVE DATA TO STORAGE
 * -------------------------------------------
 */

export function* wizardImportSubscribersLimitsUpdateStorageSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    const errorRowsAmount = yield select(makeSelectWizardImportSubscribersLimitsErrorRowsAmount());
    const fileName = yield select(makeSelectWizardImportSubscribersLimitsFileName());
    const fileRowsAmount = yield select(makeSelectWizardImportSubscribersLimitsFileRowsAmount());
    const maxImportSubscriberAmount = yield select(makeSelectWizardImportSubscribersLimitsMaxImportSubscriberAmount());

    switch (stepName) {
        case EWizardImportSubscribersLimitsSteps.SelectionStep:
        case EWizardImportSubscribersLimitsSteps.ConfirmStep:
            yield put(
                wizardStorageUpdateStepAction({
                    errorRowsAmount,
                    fileName,
                    fileRowsAmount,
                    maxImportSubscriberAmount,
                }),
            );
            break;
    }
}

export function* wizardImportSubscribersLimitsLoadDataFromStorageSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = localStorageState[basketSnapshotId];

    /**
     * Если обновили страницу обновим всю информацию, которая у нас есть
     */
    if (localStorageParams) {
        yield put(
            wizardImportSubscribersLimitsSetParamsAction({
                errorRowsAmount: localStorageParams.errorRowsAmount,
                fileName: localStorageParams.fileName,
                fileRowsAmount: localStorageParams.fileRowsAmount,
                maxImportSubscriberAmount: localStorageParams.maxImportSubscriberAmount,
            }),
        );
    }

    yield call(wizardImportSubscribersLimitsUpdateStorageSaga);
}

export function* wizardImportSubscribersLimitsStorageSaveSaga(): SagaIterator {
    const params = {};
    yield put(wizardStorageSaveStepAction({ params }));
}

export function* wizardImportSubscribersLimitsValidateSaga(): SagaIterator {
    let isStepValid = true;
    let validateError = '';
    const stepName = yield select(makeSelectWizardStepName());
    const errorRowsAmount = yield select(makeSelectWizardImportSubscribersLimitsErrorRowsAmount());
    const fileName = yield select(makeSelectWizardImportSubscribersLimitsFileName());
    const fileRowsAmount = yield select(makeSelectWizardImportSubscribersLimitsFileRowsAmount());
    const removedRowsAmount = yield select(makeSelectWizardImportSubscribersLimitsRemovedRowsAmount());

    switch (stepName) {
        case EWizardImportSubscribersLimitsSteps.SelectionStep:
            if (!fileName) {
                isStepValid = false;
            }
            break;
        case EWizardImportSubscribersLimitsSteps.ConfirmStep:
            const isServiceUser: boolean = yield select(makeSelectAuthoritiesIsServiceUser());
            if (isServiceUser) {
                const commonTranslation: ICommonTranslation = yield select(makeSelectTranslationCommon());
                validateError = commonTranslation.serviceUser.tooltip;
                isStepValid = false;
                break;
            }
            isStepValid = fileRowsAmount - errorRowsAmount - removedRowsAmount > 0;
            break;
    }
    yield put(
        wizardImportSubscribersLimitsSetStepValidAction({
            isStepValid,
            validateError,
        }),
    );
}

export function* wizardImportSubscribersLimitsAttachFileSaga(action: TWizardImportSubscribersLimitsAttachFile)
    : SagaIterator {
    const payload = action.payload;

    try {
        const options = {
            headers: {
                'accept': 'application/json;charset=UTF-8',
                'X-NODE': 'sticky',
            },
        };
        const data = new FormData();
        data.append('file', payload.file);
        const url =
            API_HOSTNAME +
            stringFormat(WIZARD, EWizardName.IMPORT_SUBSCRIBERS_LIMITS) +
            stringFormat(WIZARD_ACTION, IMPORT_SUBSCRIBERS_LIMITS_UPLOAD_DOCUMENT);
        const { fileRowsAmount, resultFileId } = yield call(requestSaga, url, EHttpMethod.POST, data, options);

        yield put(
            wizardImportSubscribersLimitsAttachFileSuccessful({
                fileName: payload.file.name,
                fileRowsAmount,
                resultFileId,
            }),
        );

        yield put(wizardImportSubscribersLimitsStartValidateAction());
        yield put(tableResetAction({ tableName: TableNameType.importSubscribersLimits }));
        yield put(wizardImportSubscribersLimitsSetParamsAction({ removedRowsAmount: 0 }));
    } catch (e) {
        yield put(wizardImportSubscribersLimitsAttachFileFailed(e));
    }
}

export function* importSubscribersLimitsFormTableFieldsSaga(): SagaIterator {
    let translationImportSubscribersLimits: IWizardImportSubscribersLimitsTranslation = yield select(
        makeSelectTranslationWizardImportSubscribersLimits(),
    );
    if (translationImportSubscribersLimits.title === '') {
        yield take(LOAD_INTERNAL_TRANSLATIONS_SUCCESS);
        translationImportSubscribersLimits = yield select(makeSelectTranslationWizardImportSubscribersLimits());
    }
    const importSubscribersLimitsFields = 
        formImportSubscribersLimitsTableFields(translationImportSubscribersLimits.table);
    yield put(loadTableFieldsSuccessful({ fields: importSubscribersLimitsFields }));
}

export function* importSubscribersLimitsLoadListSaga(action: TLoadTableListAction): SagaIterator {
    const start = action.payload?.force ?? false;
    const header: string = yield select(makeSelectWizardBasketSnapshotHeader());
    const isDensity: boolean = yield select(makeSelectIsDensity());
    const tableName: TableNameType = yield select(makeSelectorTableCurrentName());
    const list = yield select(makeSelectorTableData());
    const tableLoadSize = isDensity ? TABLE_DENSITY_LOAD_SIZE : TABLE_LOAD_SIZE;
    const translationImportSubscribersLimits: IWizardImportSubscribersLimitsTranslation = yield select(
        makeSelectTranslationWizardImportSubscribersLimits(),
    );

    const fileRowsAmount = yield select(makeSelectWizardImportSubscribersLimitsFileRowsAmount());

    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-NODE': 'sticky',
            'X-BASKET-SNAPSHOT': header,
        },
    };

    try {
        const url =
            API_HOSTNAME +
            stringFormat(WIZARD, EWizardName.IMPORT_SUBSCRIBERS_LIMITS) +
            stringFormat(WIZARD_ACTION, IMPORT_SUBSCRIBERS_LIMITS_LIST);
        const queryString = formQueryParamsString({
            from: start ? 0 : list.length,
            size: tableLoadSize,
        });
        const tableData: { fileData: IWizardImportSubscribersLimitsValidatedSubscriberResponse[] } = yield call(
            requestSaga,
            url + queryString,
            EHttpMethod.GET,
            null,
            options,
        );

        const formattedData = formImportSubscribersLimitsTableList(
            tableData.fileData,
            translationImportSubscribersLimits[EWizardImportSubscribersLimitsSteps.ConfirmStep].status,
        
        )

        yield put(
            loadTableListSuccessful({
                tableName: tableName,
                data: [...(start ? [] : list), ...formattedData],
                count: fileRowsAmount,
                basketAllIn: false,
                tableFooter: undefined,
            }),
        );
    } catch (e: any) {
        yield put(loadTableListError({ tableName: tableName, error: e.toString() }));
        yield put(wizardImportSubscribersLimitsInitValidatedListFailed(e));
    }
}

export function* wizardImportSubscribersLimitsInitValidatedListSaga() {
    yield put(setTableCurrentName(TableNameType.importSubscribersLimits));
    yield call(importSubscribersLimitsFormTableFieldsSaga);
    yield put(loadTableListAction({ force: true }));
}

export function* removeRowFromTableInConfirmationStepSaga(action: TTableRowDeleteAction): SagaIterator {
    const list = yield select(makeSelectorTableData());
    const errorRowsAmount = yield select(makeSelectWizardImportSubscribersLimitsErrorRowsAmount());
    const removedRowsAmount = yield select(makeSelectWizardImportSubscribersLimitsRemovedRowsAmount());

    const data = list[action.payload?.index];

    yield put(
        wizardImportSubscribersLimitsSetParamsAction({
            errorRowsAmount: data?.errors?.length > 0 ? errorRowsAmount - 1 : errorRowsAmount,
            removedRowsAmount: removedRowsAmount + 1,
        }),
    );
}

export function* restoreRowFromTableInConfirmationStepSaga(action: TTableRowRestoreAction): SagaIterator {
    const list = yield select(makeSelectorTableData());
    const errorRowsAmount = yield select(makeSelectWizardImportSubscribersLimitsErrorRowsAmount());
    const removedRowsAmount = yield select(makeSelectWizardImportSubscribersLimitsRemovedRowsAmount());

    const data = list[action.payload?.index];

    yield put(
        wizardImportSubscribersLimitsSetParamsAction({
            errorRowsAmount: data?.errors?.length > 0 ? errorRowsAmount + 1 : errorRowsAmount,
            removedRowsAmount: removedRowsAmount - 1,
        }),
    );
}

/*
 * -------------------------------------------
 *              Failure Handler
 * -------------------------------------------
 */
export function* wizardImportSubscribersLimitsFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const translationImportSubscribersLimits = yield select(makeSelectTranslationWizardImportSubscribersLimits());
        if (
            translationImportSubscribersLimits[EWizardImportSubscribersLimitsSteps.SelectionStep].attachError.codes[
                errorCode
            ]
        ) {
            title = 
                translationImportSubscribersLimits[EWizardImportSubscribersLimitsSteps.SelectionStep].attachError.codes[
                    errorCode
                ] || translationCommon.error.codes['unknown.error'];
            yield put(wizardImportSubscribersLimitsSetParamsAction({ fileName: '', errorCode: title }));
            return;
        } else {
            title = 
                translationImportSubscribersLimits.error.codes[errorCode] 
                || translationCommon.error.codes[errorCode] 
                || translationCommon.error.codes['unknown.error'];
            yield put(wizardImportSubscribersLimitsSetParamsAction({ fileName: '', errorCode: title }));
            return;
        }
    }
}

/*
 * -------------------------------------------
 *       Wizard Sagas
 * -------------------------------------------
 */
export function* wizardImportSubscribersLimitsSagas() {
    yield all([
        // initialization
        takeEvery(WIZARD_START_SUCCESS, wizardImportSubscribersLimitsStorageSaveSaga),
        takeEvery(WIZARD_META_SUCCESS, wizardImportSubscribersLimitsMetaSaga),
        // next
        takeEvery(WIZARD_IMPORT_SUBSCRIBERS_LIMITS_HANDLE_NEXT, wizardImportSubscribersLimitsHandleNextSaga),
        takeEvery([WIZARD_NEXT_SUCCESS, WIZARD_BACK_SUCCESS], wizardImportSubscribersLimitsLoadDataFromStorageSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, wizardImportSubscribersLimitsHandleNextSuccessSaga),
        // validate
        takeEvery(
            [WIZARD_IMPORT_SUBSCRIBERS_LIMITS_START_VALIDATE, WIZARD_IMPORT_SUBSCRIBERS_LIMITS_SET_PARAMS],
            wizardImportSubscribersLimitsValidateSaga,
        ),
        takeEvery(WIZARD_IMPORT_SUBSCRIBERS_LIMITS_SET_PARAMS, wizardImportSubscribersLimitsUpdateStorageSaga),
        takeEvery(WIZARD_IMPORT_SUBSCRIBERS_LIMITS_ATTACH_FILE, wizardImportSubscribersLimitsAttachFileSaga),
        takeEvery(
            WIZARD_IMPORT_SUBSCRIBERS_LIMITS_INIT_VALIDATED_LIST, 
            wizardImportSubscribersLimitsInitValidatedListSaga,
        ),
        //remove row
        takeEvery(TABLE_ROW_DELETE_ACTION, removeRowFromTableInConfirmationStepSaga),
        takeEvery(TABLE_ROW_RESTORE_ACTION, restoreRowFromTableInConfirmationStepSaga),
        // failed
        takeEvery(
            [
                WIZARD_BACK_FAILED,
                WIZARD_NEXT_FAILED,
                WIZARD_RESTORE_FAILED,
                WIZARD_START_FAILED,
                WIZARD_IMPORT_SUBSCRIBERS_LIMITS_ATTACH_FILE_FAILED,
                WIZARD_IMPORT_SUBSCRIBERS_LIMITS_INIT_VALIDATED_LIST_FAILED,
            ],
            wizardImportSubscribersLimitsFailedSaga,
        ),
    ]);
}
